import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import {
  projectAutomationLimitsSchema,
  type ProjectAutomationLimits,
} from "@zworks/console-contracts";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { automationLimitsQuery, saveAutomationLimits } from "@/lib/automation";

interface Draft {
  dailyBudget: string;
  quietHours: string;
  pause: string;
}

const inputClass = "h-11 max-w-xs md:h-9";

function toDraft(limits: ProjectAutomationLimits): Draft {
  return {
    dailyBudget: limits.dailyBudget === null ? "" : String(limits.dailyBudget),
    quietHours: limits.quietHours ?? "",
    pause:
      limits.pauseAfterConsecutiveFailures === null
        ? ""
        : String(limits.pauseAfterConsecutiveFailures),
  };
}

/** Empty fields mean the limit is off. Anything typed is parsed as a number or a window. */
function toLimits(draft: Draft): ProjectAutomationLimits {
  return {
    dailyBudget: draft.dailyBudget.trim() === "" ? null : Number(draft.dailyBudget),
    quietHours: draft.quietHours.trim() === "" ? null : draft.quietHours.trim(),
    pauseAfterConsecutiveFailures: draft.pause.trim() === "" ? null : Number(draft.pause),
  };
}

/**
 * Project-wide automation limits (AU16). Every automated start obeys them on top of
 * each rule's own guardrails. Runs at once and the per-run cap are project settings,
 * shown here for reference and edited in Project settings.
 */
export function AutomationLimitsCard({
  projectId,
  isOwner,
  runsAtOnce,
  perRunCap,
}: {
  projectId: string;
  isOwner: boolean;
  runsAtOnce: number;
  perRunCap: number;
}) {
  const client = useQueryClient();
  const query = useQuery(automationLimitsQuery(projectId));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [feedback, setFeedback] = useState("");
  const saved = query.data ?? null;
  const form = draft ?? (saved ? toDraft(saved) : null);
  const candidate = form ? toLimits(form) : null;
  const valid = candidate ? projectAutomationLimitsSchema.safeParse(candidate).success : false;
  const changed =
    candidate !== null && saved !== null && JSON.stringify(candidate) !== JSON.stringify(saved);
  const save = useMutation({
    mutationFn: (input: ProjectAutomationLimits) => saveAutomationLimits(projectId, input),
    onSuccess: (result) => {
      setDraft(null);
      setFeedback("Automation limits saved.");
      client.setQueryData(automationLimitsQuery(projectId).queryKey, result);
    },
    onError: () => setFeedback("The limits could not be saved. Check the values and try again."),
  });

  return (
    <section id="limits" aria-labelledby="limits-title" className="scroll-mt-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <SectionHeading id="limits-title">Limits</SectionHeading>
          <p className="text-sm text-muted-foreground">
            These limits apply to every automated start, on top of each rule&apos;s own guardrails.
            A start held by a limit is not counted as a failure.
          </p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Runs at once</dt>
            <dd className="tabular-nums">
              {runsAtOnce}{" "}
              <a
                href="/settings"
                className="ml-2 inline-flex min-h-11 items-center text-accent-text hover:underline md:min-h-0"
              >
                Change in Project settings
              </a>
            </dd>
            <dt className="text-muted-foreground">Credit cap per run</dt>
            <dd className="tabular-nums">{perRunCap}</dd>
          </dl>
          {query.isError && (
            <p role="alert" className="text-sm text-destructive-text">
              The limits could not be loaded.
            </p>
          )}
          {form && (
            <form
              className="grid gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (isOwner && changed && valid && candidate) save.mutate(candidate);
              }}
            >
              <label className="flex flex-col gap-1.5 text-sm">
                Daily budget (credits, all automations)
                <Input
                  aria-label="Daily budget"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="No limit"
                  disabled={!isOwner}
                  className={inputClass}
                  value={form.dailyBudget}
                  onChange={(event) => {
                    setFeedback("");
                    setDraft({ ...form, dailyBudget: event.target.value });
                  }}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                Quiet hours (project time zone)
                <Input
                  aria-label="Quiet hours"
                  placeholder="22:00-07:00"
                  disabled={!isOwner}
                  className={inputClass}
                  value={form.quietHours}
                  onChange={(event) => {
                    setFeedback("");
                    setDraft({ ...form, quietHours: event.target.value });
                  }}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                Pause a rule after consecutive failures (1 to 10)
                <Input
                  aria-label="Pause after consecutive failures"
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  placeholder="Rule setting"
                  disabled={!isOwner}
                  className={inputClass}
                  value={form.pause}
                  onChange={(event) => {
                    setFeedback("");
                    setDraft({ ...form, pause: event.target.value });
                  }}
                />
              </label>
              {isOwner ? (
                <div className="flex justify-end">
                  <Button type="submit" disabled={save.isPending || !changed || !valid}>
                    {save.isPending ? "Saving…" : "Save limits"}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Only an owner can change these limits.
                </p>
              )}
            </form>
          )}
          {feedback && (
            <p role="status" className="text-sm text-muted-foreground">
              {feedback}
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
